import {
    ITextFieldWithActionStyleProps,
    ITextFieldWithActionStyles,
} from './TextFieldWithAction.types';
import {
    getGlobalClassNames,
    getInputFocusStyle,
    IStyle,
    HighContrastSelector,
    IButtonStyles,
    memoizeFunction,
    ITheme,
    IRawStyle,
    getHighContrastNoAdjustStyle,
    keyframes,
} from '@fluentui/react';

const GlobalClassNames = {
    root: 'ms-TextFieldWithAction-root',
    textInput: 'ms-TextFieldWithAction-textInput',
};

const getDisabledStyles = memoizeFunction((theme: ITheme): IRawStyle => {
    const { semanticColors } = theme;

    return {
        backgroundColor: semanticColors.disabledBackground,
        color: semanticColors.disabledText,
        cursor: 'default',
        selectors: {
            ':after': {
                borderColor: semanticColors.disabledBackground,
            },
            [HighContrastSelector]: {
                color: 'GrayText',
                backgroundColor: 'transparent',
                selectors: {
                    ':after': {
                        borderColor: 'GrayText',
                    },
                },
                ...getHighContrastNoAdjustStyle(),
            },
        },
    };
});
const buttonHighContrastStyles: IStyle = {
    selectors: {
        [HighContrastSelector]: {
            backgroundColor: 'Highlight',
            borderColor: 'Highlight',
            color: 'HighlightText',
            ...getHighContrastNoAdjustStyle(),
        },
    },
};
export const getButtonStyles = (theme: ITheme): IButtonStyles => {
    const caret = {
        buttonTextColor: theme.semanticColors.bodySubtext,
        buttonTextHoveredCheckedColor: theme.semanticColors.buttonTextChecked,
        buttonBackgroundHoveredColor: theme.semanticColors.listItemBackgroundHovered,
        buttonBackgroundCheckedColor: theme.semanticColors.listItemBackgroundChecked,
        buttonBackgroundCheckedHoveredColor: theme.semanticColors.listItemBackgroundCheckedHovered,
    };
    return {
        icon: {
            display: 'flex',
            width: 12,
            height: 12,
        },
        root: {
            color: caret.buttonTextColor,
            padding: '0px 2px',
            borderRadius: 0,
            selectors: {
                [HighContrastSelector]: {
                    color: 'ButtonText',
                    ...getHighContrastNoAdjustStyle(),
                },
            },
        },
        rootHovered: [
            {
                backgroundColor: caret.buttonBackgroundHoveredColor,
                color: caret.buttonTextHoveredCheckedColor,
            },
            buttonHighContrastStyles,
        ],
        rootPressed: [
            {
                backgroundColor: caret.buttonBackgroundCheckedColor,
                color: caret.buttonTextHoveredCheckedColor,
            },
            buttonHighContrastStyles,
        ],
        rootChecked: [
            {
                backgroundColor: caret.buttonBackgroundCheckedColor,
                color: caret.buttonTextHoveredCheckedColor,
            },
            buttonHighContrastStyles,
        ],
        rootCheckedHovered: [
            {
                backgroundColor: caret.buttonBackgroundCheckedHoveredColor,
                color: caret.buttonTextHoveredCheckedColor,
            },
            buttonHighContrastStyles,
        ],
        rootDisabled: [getDisabledStyles(theme)],
    };
};

const highContrastBorderStyle: IRawStyle = {
    borderColor: 'Highlight',
    ...getHighContrastNoAdjustStyle(),
    color: 'HighlightText',
};

const movementTimingFunction = 'cubic-bezier(0.33, 0.00, 0.10, 1.00)';
const prefixEnterKeyframes = memoizeFunction(() => {
    return keyframes({
        '0%': { width: '0px', opacity: '0%', animationTimingFunction: movementTimingFunction },
        '66%': { width: '16px', opacity: '0%', animationTimingFunction: 'linear' },
        '100%': { opacity: '100%' },
    });
});

const prefixExitKeyframes = memoizeFunction(() => {
    return keyframes({
        '0%': { width: '16px', opacity: '100%', animationTimingFunction: 'linear' },
        '33%': { width: '16px', opacity: '0%', animationTimingFunction: movementTimingFunction },
        '100%': { width: '0px' },
    });
});

export const getStyles = (props: ITextFieldWithActionStyleProps): ITextFieldWithActionStyles => {
    const { hideFocusVisual, className, isActive, theme, disabled, prefixExiting } = props;
    const classNames = getGlobalClassNames(GlobalClassNames, theme);
    const focusStyle = getInputFocusStyle(
        theme.semanticColors.inputFocusBorderAlt,
        theme.effects.roundedCorner2
    );
    const { semanticColors } = theme;

    return {
        root: [
            classNames.root,
            {
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                alignItems: 'stretch',
                position: 'relative',
                border: `1px solid ${semanticColors.inputBorder}`,
                borderRadius: '2px',
                borderStyle: 'solid',
                boxSizing: 'border-box',
                backgroundColor: semanticColors.inputBackground,
            },
            isActive &&
                !disabled &&
                !hideFocusVisual && [
                    focusStyle,
                    {
                        selectors: {
                            [HighContrastSelector]: highContrastBorderStyle,
                        },
                    },
                ],
            disabled && [
                {
                    borderColor: semanticColors.disabledBorder,
                    selectors: {
                        [HighContrastSelector]: {
                            borderColor: 'GrayText',
                            ...getHighContrastNoAdjustStyle(),
                        },
                    },
                },
                isActive && {
                    selectors: {
                        [HighContrastSelector]: {
                            outline: 'GrayText dotted 2px',
                            ...getHighContrastNoAdjustStyle(),
                        },
                    },
                },
            ],
            {
                selectors: {
                    [HighContrastSelector]: [
                        {
                            backgroundColor: 'Window',
                        },
                        !disabled && {
                            selectors: {
                                ':hover': highContrastBorderStyle,
                            },
                        },
                        getHighContrastNoAdjustStyle(),
                    ],
                },
            },
            className,
        ],
        textInput: [
            classNames.textInput,
            {
                width: '100%',
                minWidth: 0,
                borderWidth: 0,
                outline: 0,
                margin: 0,
                padding: '0px 0px 0px 2px',
                boxShadow: 'none',
                textOverflow: 'ellipsis',
                backgroundColor: 'transparent',
                color: semanticColors.inputText,
                selectors: {
                    '&:active, &:focus, &:hover': { outline: 0 },
                    '::-ms-clear': {
                        display: 'none',
                    },
                    [HighContrastSelector]: {
                        background: 'Window',
                        color: disabled ? 'GrayText' : 'WindowText',
                        ...getHighContrastNoAdjustStyle(),
                    },
                },
            },
            disabled && {
                backgroundColor: semanticColors.disabledBackground,
                color: semanticColors.disabledText,
            },
            theme.fonts.medium,
        ],
        prefix: {
            wordWrap: 'none',
            padding: '0px 0px 0px 2px',
            alignSelf: 'center',
            width: 16,
            height: 16,
            color: !disabled ? semanticColors.inputPlaceholderText : semanticColors.disabledText,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            selectors: {
                [HighContrastSelector]: {
                    color: disabled ? 'GrayText' : 'WindowText',
                    ...getHighContrastNoAdjustStyle(),
                },
            },
            animationName: `${prefixExiting ? prefixExitKeyframes() : prefixEnterKeyframes()}`,
            animationDuration: '300ms',
            opacity: prefixExiting ? '0%' : '100%',
        },
        subComponentStyles: {
            buttonStyles: getButtonStyles(theme),
        },
    };
};
